<script setup lang="ts">
import { useAuth0 } from "@auth0/auth0-vue";
import {
  IonApp,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonLabel,
  IonIcon,
  IonRouterOutlet,
  IonToast,
} from "@ionic/vue";
import { calendar, home, location, people } from "ionicons/icons";
import { storeToRefs } from "pinia";
import { onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { useAuthStore } from "@/stores/auth";
import { useDocumentStore } from "@/stores/document";
import { usePwaUpdate } from "@/composables/usePwaUpdate";

const auth0 = useAuth0();
const authStore = useAuthStore();
const documentStore = useDocumentStore();
const router = useRouter();
const { t } = useI18n();

const { hasValidCache, isOnline, databaseName } = storeToRefs(authStore);

const { needRefresh, updateAvailable, applyUpdate, dismissUpdate } =
  usePwaUpdate();

watch(needRefresh, needsUpdate => {
  if (needsUpdate) {
    updateAvailable.value = true;
  }
});

const updateToastButtons = [
  {
    text: t("app.dismiss"),
    role: "cancel",
    handler: dismissUpdate,
  },
  {
    text: t("app.update"),
    handler: applyUpdate,
  },
];

async function revalidateSession(): Promise<boolean> {
  if (auth0.isAuthenticated.value && auth0.user.value) {
    authStore.cacheUser(auth0.user.value);
    return true;
  }

  try {
    await auth0.getAccessTokenSilently();
    if (auth0.isAuthenticated.value && auth0.user.value) {
      authStore.cacheUser(auth0.user.value);
      return true;
    }
  } catch {
    authStore.clearUser();
    await documentStore.teardown();
    return false;
  }

  return false;
}

onMounted(() => {
  authStore.init();

  watch(
    isOnline,
    async isOnline => {
      if (!isOnline) {
        return;
      }
      const isValid = await revalidateSession();
      if (!isValid) {
        router.push("/login");
      }
    },
    { immediate: true },
  );

  watch(
    databaseName,
    async databaseName => {
      if (databaseName) {
        await documentStore.init(databaseName);
        if (import.meta.env.DEV) {
          documentStore.loadSampleData();
        }
        const remoteUrl = import.meta.env.VITE_COUCHDB_URL;
        if (remoteUrl) {
          const isProductionDb = remoteUrl.includes("couch.hoofvision.app");
          if (import.meta.env.DEV && isProductionDb) {
            console.error(
              "[Database] Refusing to sync with production database in dev mode!",
            );
          } else {
            documentStore.startSync({
              remoteUrl,
              getToken: () => auth0.getAccessTokenSilently(),
            });
          }
        }
      } else {
        await documentStore.teardown();
      }
    },
    { immediate: true },
  );
});
</script>

<template>
  <ion-app>
    <ion-toast
      :is-open="updateAvailable"
      :message="t('app.update_available')"
      position="top"
      :buttons="updateToastButtons"
      @didDismiss="dismissUpdate"
    />

    <template v-if="!hasValidCache">
      <ion-router-outlet></ion-router-outlet>
    </template>

    <template v-else>
      <ion-tabs>
        <ion-router-outlet></ion-router-outlet>
        <ion-tab-bar slot="bottom">
          <ion-tab-button tab="dashboard" href="/dashboard">
            <ion-icon aria-hidden="true" :icon="home" />
            <ion-label>{{ $t("tab.dashboard") }}</ion-label>
          </ion-tab-button>

          <ion-tab-button tab="calendar" href="/calendar">
            <ion-icon aria-hidden="true" :icon="calendar" />
            <ion-label>{{ $t("tab.calendar") }}</ion-label>
          </ion-tab-button>

          <ion-tab-button tab="clients" href="/clients">
            <ion-icon aria-hidden="true" :icon="people" />
            <ion-label>{{ $t("tab.clients") }}</ion-label>
          </ion-tab-button>

          <ion-tab-button tab="stables" href="/stables">
            <ion-icon aria-hidden="true" :icon="location" />
            <ion-label>{{ $t("tab.stables") }}</ion-label>
          </ion-tab-button>

          <ion-tab-button tab="horses" href="/horses">
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="currentColor"
              stroke="none"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              style="margin-bottom: 2px"
            >
              <g transform="matrix(0.24,0,0,0.24,-2.4372815e-7,3.3215993)">
                <path
                  d="m 17.406956,56.127804 c 6.232377,1.028174 11.000877,-4.300162 14.55084,-8.619008 3.718923,-6.083623 10.11505,0.635722 10.056495,5.587367 2.607553,10.299441 -3.481347,23.884181 -8.412339,32.73495 0.140812,0.39331 2.084247,0.31164 4.376105,0.32889 h 61.717131 c 1.295512,-11.901492 -1.54207,-24.213574 -8.44625,-34.052472 -1.52811,-2.144644 -5.44276,-8.528229 -0.0136,-5.77535 1.78947,1.332632 5.71246,2.214288 3.13377,-1.086922 C 89.225218,35.174381 80.046789,28.117987 71.232339,21.423501 69.393266,17.682428 80.632823,21.465927 75.040678,17.868967 64.525825,8.997934 49.791833,5.373596 36.362888,8.104209 30.482307,7.505841 30.124056,-0.679956 24.698289,-2.014837 c -4.130316,0.07529 0.266764,9.451402 -3.538057,4.649174 -3.712122,-3.459007 -2.701196,2.21148 -2.814511,4.516902 l -0.151435,8.668483 C 9.7279424,24.2828 9.7471034,37.321583 3.0174594,46.856633 c -3.00400002,4.356453 -4.781943,11.408855 -0.342349,15.45873 3.864897,3.734381 11.4109486,3.291672 13.5250376,-2.141006 0.634684,-1.265405 1.013586,-2.647709 1.206808,-4.046553 z"
                />
              </g>
            </svg>
            <ion-label>{{ $t("tab.horses") }}</ion-label>
          </ion-tab-button>
        </ion-tab-bar>
      </ion-tabs>
    </template>
  </ion-app>
</template>
